<script setup lang="ts">
import { computed, ref } from 'vue'
import type { EdgeMapFund, EdgeMapResponse, EdgePoint } from '../../api/client'
import { FUND_META } from '../fundPalette'
import { futureColor } from './futurePalette'
import {
  PLOT, HORIZONS, MEASURES, CIRCLES, AXES, type Horizon, type Scope, type Quadrant, type Measure, type Circle,
  scaleFor, px, py, ticks, pathThrough, inScope, viewPoint, bubbleRadius, quadrantIn,
} from './edgeMapGeometry'
import { hoverRows } from './edgeSourceFormat'

// Edge map (spec 2026-10-30 §6): per fund, stale-pricing return (vertical) against post-decision continuation
// (horizontal), bubble size = rule payoff, a dashed ghost N ago joined by a path through the shorter horizons.
// 2026-10-31 (paolo): a View toggle (Return | Per unit of risk: every figure divided by the window's NAV volatility,
// a Sharpe-style ratio) and a Circle toggle (Payoff | CAGR, the rule's compounded annual return).
// Under Risk → Live vs backtest (FOF view). The host fetches the data and owns the window.
// `window` is the host's window (the source of truth, even while a fetch for it is failing or in flight).
const props = defineProps<{ data: EdgeMapResponse | null; loading: boolean; error: string | null; window: 120 | 250 }>()
const emit = defineEmits<{ (e: 'select', fundId: string): void; (e: 'window', w: 120 | 250): void }>()

const HORIZON_KEY = 'sw.risk.edge.horizon'
const SCOPE_KEY = 'sw.risk.edge.scope'
const MEASURE_KEY = 'sw.risk.edge.measure'
const CIRCLE_KEY = 'sw.risk.edge.circle'
const SCOPES: readonly Scope[] = ['Live', 'Candidates', 'All']
const MEASURE_LABEL: Record<Measure, string> = { return: 'Return', risk: 'Per unit of risk' }
const CIRCLE_LABEL: Record<Circle, string> = { payoff: 'Payoff', cagr: 'CAGR' }
const WINDOWS: readonly (120 | 250)[] = [120, 250]

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
  } catch { return fallback }
}
function writeStored(key: string, v: string) {
  try { localStorage.setItem(key, v) } catch { /* private window / blocked storage: keep in memory only */ }
}

const horizon = ref<Horizon>(readStored(HORIZON_KEY, HORIZONS, '1y'))
const scope = ref<Scope>(readStored(SCOPE_KEY, SCOPES, 'All'))
// 2026-10-31 (paolo): per unit of risk is the default view; a stored choice still wins.
const measure = ref<Measure>(readStored(MEASURE_KEY, MEASURES, 'risk'))
const circle = ref<Circle>(readStored(CIRCLE_KEY, CIRCLES, 'payoff'))
const legendOpen = ref(false)
function setHorizon(h: Horizon) { horizon.value = h; writeStored(HORIZON_KEY, h) }
function setScope(s: Scope) { scope.value = s; writeStored(SCOPE_KEY, s) }
function setMeasure(m: Measure) { measure.value = m; writeStored(MEASURE_KEY, m) }
function setCircle(c: Circle) { circle.value = c; writeStored(CIRCLE_KEY, c) }

const spec = computed(() => AXES[measure.value])
const vp = (p: EdgePoint) => viewPoint(p, measure.value)
const drawable = (ps: (EdgePoint | null)[]) => ps.filter((p): p is EdgePoint => p !== null)
const route = (f: EdgeMapFund) => drawable(pathThrough(f, horizon.value).map(vp))

const inView = computed<EdgeMapFund[]>(() => (props.data?.funds ?? []).filter(f => inScope(f, scope.value)))
// In the risk view a fund needs a stored volatility (rows written before V159 have none until the backfill).
const funds = computed<EdgeMapFund[]>(() => inView.value.filter(f => vp(f.now) !== null))
const hiddenNoVol = computed(() => inView.value.length - funds.value.length)
const scale = computed(() => scaleFor(funds.value.flatMap(f => [vp(f.now)!, ...route(f)]), spec.value))

interface Bubble {
  id: string; label: string; color: string; candidate: boolean; red: boolean
  x: number; y: number; r: number; dark: boolean
  ghost: { x: number; y: number; r: number } | null
  path: string | null; dots: { x: number; y: number }[]
  fund: EdgeMapFund
}

function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  return 0.299 * r + 0.587 * g + 0.114 * b > 170
}

// Colour = the future that drives the fund, so funds on one future share a colour (one bet, one hue).
const bubbles = computed<Bubble[]>(() => funds.value.map(f => {
  const color = futureColor(f.future)
  const s = scale.value
  const at = (p: EdgePoint) => ({ x: px(s, p.continuationBp), y: py(s, p.staleBp) })
  const nowView = vp(f.now)!
  const now = at(nowView)
  const path = route(f)
  const ghostRaw = f.horizons[horizon.value]
  const ghostPt = ghostRaw ? vp(ghostRaw) : null
  const r = (p: EdgePoint) => bubbleRadius(p, measure.value, circle.value)
  return {
    // palette funds use their short palette name; a fund the palette does not know uses the server's label
    id: f.fundId, label: FUND_META[f.fundId]?.label ?? f.label, color,
    candidate: f.status === 'candidate', red: f.pricingRag === 'red',
    ...now, r: r(nowView), dark: isLight(color),
    ghost: ghostPt ? { ...at(ghostPt), r: r(ghostPt) } : null,
    // the path starts at the ghost; when the ghost cannot be drawn the route is not drawn either
    path: ghostPt && path.length ? path.map(p => { const q = at(p); return `${q.x.toFixed(1)},${q.y.toFixed(1)}` }).join(' ') : null,
    dots: ghostPt ? path.slice(1, -1).map(at) : [],
    fund: f,
  }
}))

// Bigger bubbles first, so a small bubble drawn on top of a big one stays clickable.
// One key chip per future on the map, in first-seen order.
const futureKey = computed(() => {
  const seen = new Map<string, string>()
  for (const f of funds.value) {
    const k = f.future?.toUpperCase() ?? '?'
    if (!seen.has(k)) seen.set(k, futureColor(f.future))
  }
  return [...seen].map(([future, color]) => ({ future, color }))
})

const drawOrder = computed(() => [...bubbles.value].sort((a, b) => b.r - a.r))

const boundary = computed(() => spec.value.boundary / scale.value.factor)
const qx = computed(() => Math.min(Math.max(px(scale.value, boundary.value), PLOT.x0), PLOT.x1))
const qy = computed(() => Math.min(Math.max(py(scale.value, boundary.value), PLOT.y0), PLOT.y1))
const strongY = computed(() => py(scale.value, spec.value.strong / scale.value.factor))
const fx = computed(() => (qx.value - PLOT.x0) / (PLOT.x1 - PLOT.x0))
const xTicks = computed(() => ticks(scale.value.cx[0], scale.value.cx[1]).map(v => ({ v, x: px(scale.value, v / scale.value.factor) })))
const yTicks = computed(() => ticks(scale.value.sy[0], scale.value.sy[1]).map(v => ({ v, y: py(scale.value, v / scale.value.factor) })))
const signed = (v: number) => `${v > 0 ? '+' : ''}${v}${measure.value === 'return' ? '%' : ''}`
const fmtLine = (v: number) => (measure.value === 'return' ? `${v} %/yr` : `${v}`)
const axisUnit = computed(() => (measure.value === 'return' ? '% per year' : 'per unit of risk'))
const strongText = computed(() => (measure.value === 'return'
  ? `strong · ${spec.value.strong} %/yr from stale pricing alone`
  : `strong · ${spec.value.strong} × risk from stale pricing alone`))

const QUADRANTS: { key: Quadrant; short: string; long: string }[] = [
  { key: 'staleEdge', short: 'STALE-PRICING EDGE', long: ' · the valuer pays us, market not needed' },
  { key: 'staleTrend', short: 'STALE PRICING + TREND', long: ' · best place to be' },
  { key: 'noEdge', short: 'NO EDGE', long: '' },
  { key: 'trendPaid', short: 'TREND-PAID', long: ' · no stale pricing · diversifies the mechanism · correlated' },
]
const quadrantLabels = computed(() => QUADRANTS.map(q => {
  const right = q.key === 'staleTrend' || q.key === 'trendPaid'
  const top = q.key === 'staleEdge' || q.key === 'staleTrend'
  return {
    ...q,
    x: right ? qx.value + 8 : PLOT.x0 + 8,
    y: top ? PLOT.y0 + 16 : PLOT.y1 - 8,
    // the pot figure sits on its own line (below a top label, above a bottom one), left-aligned with it
    wy: top ? PLOT.y0 + 38 : PLOT.y1 - 30, // 22 units apart: clear even at the phone's 18-unit font
    weight: Math.round(quadrantWeights.value[q.key] ?? 0),
  }
}))

// Corner pot figures: the server's split in the return view; in the risk view the funds move against the risk
// boundary, so the split is recomputed here from the live funds' weights (a live fund without volatility is left out).
const quadrantWeights = computed<Partial<Record<Quadrant, number>>>(() => {
  if (measure.value === 'return') return props.data?.quadrantWeights ?? {}
  const out: Partial<Record<Quadrant, number>> = {}
  for (const f of props.data?.funds ?? []) {
    if (f.status !== 'live') continue
    const v = vp(f.now)
    if (!v) continue
    const q = quadrantIn(v, spec.value)
    out[q] = (out[q] ?? 0) + f.weightPct
  }
  return out
})

// Mouse: hover shows a floating tooltip, click opens the fund. Touch (no hover): the first tap pins a details panel
// under the map with an "Open fund" button; a second tap on the same bubble also opens it.
const hover = ref<{ id: string; x: number; y: number } | null>(null)
const pinned = ref<string | null>(null)
let touched = false
const host = ref<HTMLElement | null>(null)
function onHover(id: string, ev: MouseEvent) {
  const box = host.value?.getBoundingClientRect()
  hover.value = { id, x: ev.clientX - (box?.left ?? 0) + 12, y: ev.clientY - (box?.top ?? 0) + 12 }
}
function onTouch() { touched = true; hover.value = null }
function onBubbleClick(id: string) {
  if (touched) {
    touched = false
    if (pinned.value !== id) { pinned.value = id; return }
  }
  emit('select', id)
}
const pinnedView = computed(() => {
  const b = pinned.value && bubbles.value.find(x => x.id === pinned.value)
  if (!b) return null
  return { b, rows: hoverRows(b.fund.now, b.fund.horizons[horizon.value] ?? null, horizon.value, measure.value) }
})
const hovered = computed(() => {
  const b = hover.value && bubbles.value.find(x => x.id === hover.value!.id)
  if (!b) return null
  return { b, rows: hoverRows(b.fund.now, b.fund.horizons[horizon.value] ?? null, horizon.value, measure.value) }
})
</script>

<template>
  <div ref="host" class="edge-map" data-testid="edge-map">
    <div class="edge-head">
      <h3 class="edge-title">Edge map — where each fund's return comes from</h3>
      <button type="button" class="kt-info-btn" :aria-expanded="legendOpen" aria-controls="edge-legend"
        :aria-label="legendOpen ? 'Hide explanation' : 'Show explanation'" @click="legendOpen = !legendOpen">i</button>
    </div>

    <section v-if="legendOpen" id="edge-legend" class="kt-legend" role="region" aria-label="Edge map explanation"
      data-testid="edge-legend">
      <p class="kt-legend-lead"><b>Stale-pricing return</b> (vertical): the part of the fund's next NAV return explained by
        the 08:40 futures move its valuer had not yet priced when we dealt — the strategy's structural edge.
        <b>Continuation</b> (horizontal): the market carrying on after 08:40 — momentum, paid only while markets trend.
        Both are % per year, fitted over the last {{ data?.window ?? 250 }} decision days on each fund's live preset.</p>
      <p class="kt-legend-lead"><b>View</b>: <b>Return</b> plots % per year. <b>Per unit of risk</b> divides each figure by
        the fund's NAV volatility over the same window (annual return ÷ annual volatility, like a Sharpe ratio), so a calm
        fund and a volatile fund on the same future compare fairly. <b>Circle</b>: <b>Payoff</b> is the rule's total
        payoff; <b>CAGR</b> is its compounded annual return (in when the signal says buy, cash otherwise, no costs),
        drawn on the same scale as payoff, so a CAGR circle smaller than the payoff circle is the volatility drag.</p>
      <p class="kt-legend-lead"><b>Bubble colour</b> = the future that drives the fund: funds on the same future share a colour.
        <b>Bubble size</b> = the rule's total payoff (or CAGR). <b>Dashed outline</b> = the same fund at the
        chosen horizon; the line and dots trace its route through the shorter horizons to today. A <b>red dashed ring</b>
        means the NAV-tab pricing-source detector is Red. A <b>dashed bubble</b> is a candidate: a fund you can see
        but hold no weight in.</p>
      <p class="kt-legend-lead">Boundary lines at {{ fmtLine(spec.boundary) }} on both axes; the faint green line marks
        {{ fmtLine(spec.strong) }} from stale pricing alone. Corner figures are the share of your live pot in each zone.</p>
    </section>

    <div class="edge-ctl" data-testid="edge-map-controls">
      <span>Ghost</span>
      <button v-for="h in HORIZONS" :key="h" type="button" class="edge-btn" :class="{ on: h === horizon }"
        :data-testid="`edge-horizon-${h}`" @click="setHorizon(h)">{{ h }}</button>
      <span class="edge-gap">Window</span>
      <button v-for="w in WINDOWS" :key="w" type="button" class="edge-btn" :class="{ on: w === window }"
        :data-testid="`edge-window-${w}`" @click="emit('window', w)">{{ w }}d</button>
      <span class="edge-gap">Show</span>
      <button v-for="s in SCOPES" :key="s" type="button" class="edge-btn" :class="{ on: s === scope }"
        :data-testid="`edge-scope-${s}`" @click="setScope(s)">{{ s }}</button>
      <span class="edge-gap">View</span>
      <button v-for="m in MEASURES" :key="m" type="button" class="edge-btn" :class="{ on: m === measure }"
        :data-testid="`edge-measure-${m}`" @click="setMeasure(m)">{{ MEASURE_LABEL[m] }}</button>
      <span class="edge-gap">Circle</span>
      <button v-for="c in CIRCLES" :key="c" type="button" class="edge-btn" :class="{ on: c === circle }"
        :data-testid="`edge-circle-${c}`" @click="setCircle(c)">{{ CIRCLE_LABEL[c] }}</button>
    </div>

    <div v-if="futureKey.length" class="edge-key" data-testid="edge-future-key">
      <span>Colour = future</span>
      <span v-for="k in futureKey" :key="k.future" class="edge-key-item" :data-testid="`edge-key-${k.future}`">
        <i :style="{ background: k.color }" />{{ k.future }}</span>
    </div>

    <p v-if="hiddenNoVol > 0 && !error" class="edge-note" data-testid="edge-hidden-novol">
      {{ hiddenNoVol }} fund{{ hiddenNoVol === 1 ? '' : 's' }} not shown: volatility not computed yet.</p>
    <p v-if="error" class="edge-error">Edge map unavailable ({{ error }}).</p>
    <p v-else-if="loading && !data" class="edge-note">Loading…</p>
    <p v-else-if="!data || data.funds.length === 0" class="edge-note" data-testid="edge-empty">
      No history yet — the nightly edge-source job fills this after its first run.</p>

    <svg v-else :viewBox="`0 0 ${PLOT.width} ${PLOT.height}`" class="edge-svg" :class="{ 'is-loading': loading }" data-testid="edge-svg"
      role="img" aria-label="Edge map">
      <defs>
        <linearGradient id="edge-g-bottom" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="rgba(244,67,54,0.14)" />
          <stop :offset="fx" stop-color="rgba(255,152,0,0.09)" />
          <stop :offset="Math.min(1, fx + 0.45)" stop-color="rgba(76,175,80,0.08)" />
          <stop offset="1" stop-color="rgba(76,175,80,0.14)" />
        </linearGradient>
        <linearGradient id="edge-g-top" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="rgba(76,175,80,0.05)" />
          <stop :offset="fx" stop-color="rgba(76,175,80,0.07)" />
          <stop offset="1" stop-color="rgba(76,175,80,0.16)" />
        </linearGradient>
        <linearGradient id="edge-g-up" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stop-color="rgba(76,175,80,0)" />
          <stop offset="1" stop-color="rgba(76,175,80,0.12)" />
        </linearGradient>
      </defs>
      <rect :x="PLOT.x0" :y="qy" :width="PLOT.x1 - PLOT.x0" :height="PLOT.y1 - qy" fill="url(#edge-g-bottom)" />
      <rect :x="PLOT.x0" :y="PLOT.y0" :width="PLOT.x1 - PLOT.x0" :height="qy - PLOT.y0" fill="url(#edge-g-top)" />
      <rect :x="PLOT.x0" :y="PLOT.y0" :width="PLOT.x1 - PLOT.x0" :height="qy - PLOT.y0" fill="url(#edge-g-up)" />

      <line :x1="PLOT.x0" :y1="PLOT.y1" :x2="PLOT.x1" :y2="PLOT.y1" stroke="#444" />
      <line :x1="PLOT.x0" :y1="PLOT.y0" :x2="PLOT.x0" :y2="PLOT.y1" stroke="#444" />
      <line :x1="qx" :y1="PLOT.y0" :x2="qx" :y2="PLOT.y1" stroke="#3a3a3a" stroke-dasharray="3 3" />
      <line :x1="PLOT.x0" :y1="qy" :x2="PLOT.x1" :y2="qy" stroke="#3a3a3a" stroke-dasharray="3 3" />
      <template v-if="strongY > PLOT.y0 && strongY < qy">
        <line :x1="PLOT.x0" :y1="strongY" :x2="PLOT.x1" :y2="strongY" stroke="#2f4a2f" stroke-dasharray="2 4" />
        <text :x="PLOT.x1 - 6" :y="strongY - 4" class="edge-strong" text-anchor="end">{{ strongText }}</text>
      </template>

      <text v-for="t in yTicks" :key="`y${t.v}`" :x="PLOT.x0 - 8" :y="t.y + 4" class="edge-tick" text-anchor="end">{{ signed(t.v) }}</text>
      <text v-for="t in xTicks" :key="`x${t.v}`" :x="t.x" :y="PLOT.y1 + 16" class="edge-tick" text-anchor="middle">{{ signed(t.v) }}</text>
      <text :x="(PLOT.x0 + PLOT.x1) / 2" :y="PLOT.y1 + 38" class="edge-axis" text-anchor="middle">Post-decision continuation (momentum), {{ axisUnit }} →</text>
      <text x="22" :y="(PLOT.y0 + PLOT.y1) / 2" class="edge-axis" text-anchor="middle"
        :transform="`rotate(-90 22 ${(PLOT.y0 + PLOT.y1) / 2})`">Stale-pricing return, {{ axisUnit }} →</text>

      <g v-for="q in quadrantLabels" :key="q.key">
        <text :x="q.x" :y="q.y" class="edge-quadrant" :data-testid="`edge-quadrant-label-${q.key}`">{{ q.short }}<tspan class="edge-quadrant-long">{{ q.long }}</tspan></text>
        <text :x="q.x" :y="q.wy" class="edge-quadrant-weight"
          :data-testid="`edge-quadrant-weight-${q.key}`">pot {{ q.weight }} %</text>
      </g>

      <g v-for="b in bubbles" :key="`p${b.id}`">
        <template v-if="b.path">
          <polyline :points="b.path" fill="none" :stroke="b.color" stroke-width="1" opacity="0.5"
            :data-testid="`edge-path-${b.id}`" />
          <circle v-for="(d, i) in b.dots" :key="i" :cx="d.x" :cy="d.y" r="2.2" :fill="b.color" opacity="0.75" />
        </template>
      </g>
      <circle v-for="b in bubbles.filter(x => x.ghost)" :key="`g${b.id}`" :cx="b.ghost!.x" :cy="b.ghost!.y"
        :r="b.ghost!.r" fill="none" :stroke="b.color" stroke-dasharray="3 3" opacity="0.6"
        :data-testid="`edge-ghost-${b.id}`" />
      <circle v-for="b in bubbles.filter(x => x.red)" :key="`r${b.id}`" :cx="b.x" :cy="b.y" :r="b.r + 4"
        fill="none" stroke="#f44336" stroke-width="2" stroke-dasharray="4 3" :data-testid="`edge-ring-${b.id}`" />
      <circle v-for="b in drawOrder" :key="`b${b.id}`" :cx="b.x" :cy="b.y" :r="b.r" :fill="b.color"
        :fill-opacity="b.candidate ? 0.35 : 0.55" :stroke="b.color" :stroke-dasharray="b.candidate ? '3 2' : undefined"
        class="edge-bubble" :data-testid="`edge-bubble-${b.id}`" tabindex="0" role="button" :aria-label="b.label"
        @click="onBubbleClick(b.id)" @keydown.enter="emit('select', b.id)"
        @mousemove="onHover(b.id, $event)" @touchstart.passive="onTouch" @mouseleave="hover = null" />
      <text v-for="b in drawOrder" :key="`l${b.id}`" :x="b.x" :y="b.r >= 12 ? b.y + 4 : b.y + b.r + 12"
        :fill="b.r >= 12 && b.dark ? '#111' : '#e6edf3'" class="edge-label" text-anchor="middle"
        :data-testid="`edge-label-${b.id}`">{{ b.label }}</text>
    </svg>

    <div v-if="pinnedView" class="edge-tap" data-testid="edge-tap-panel">
      <div class="edge-tap-head">
        <b :style="{ color: pinnedView.b.color }">{{ pinnedView.b.label }}</b>
        <button type="button" class="edge-btn on" data-testid="edge-tap-open" @click="emit('select', pinnedView.b.id)">Open fund</button>
        <button type="button" class="edge-btn" aria-label="Close" @click="pinned = null">✕</button>
      </div>
      <div v-for="r in pinnedView.rows" :key="r.label"><span class="edge-tip-k">{{ r.label }}</span> {{ r.value }}</div>
    </div>

    <div v-if="hovered" class="edge-tip" data-testid="edge-tooltip" :style="{ left: `${hover!.x}px`, top: `${hover!.y}px` }">
      <b :style="{ color: hovered.b.color }">{{ hovered.b.label }}</b>
      <span class="edge-tip-sub">{{ hovered.b.candidate ? 'candidate' : `live · ${Math.round(hovered.b.fund.weightPct)} % of pot` }}
        · invested {{ Math.round(hovered.b.fund.now.investedPct) }} % · n {{ hovered.b.fund.now.n }}</span>
      <div v-for="r in hovered.rows" :key="r.label"><span class="edge-tip-k">{{ r.label }}</span> {{ r.value }}</div>
    </div>
  </div>
</template>

<style scoped>
.edge-map { position: relative; margin-top: 1.25rem; }
.edge-head { display: flex; align-items: center; gap: 0.5rem; }
.edge-title { margin: 0; font-size: 14px; color: #ccc; }
.edge-ctl { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin: 8px 0; font-size: 12px; color: #888; }
.edge-gap { margin-left: 10px; }
.edge-btn { padding: 2px 10px; border: 1px solid #333; border-radius: 10px; color: #999; background: transparent; cursor: pointer; font: inherit; }
.edge-btn.on { border-color: #58a6ff; background: #2a3f55; color: #e6edf3; }
.edge-svg.is-loading { opacity: 0.5; transition: opacity 0.2s; }
.edge-tap { background: #1c1c1c; border: 1px solid #333; border-radius: 4px; padding: 6px 8px; margin-top: 6px; font-size: 12px; color: #ddd; }
.edge-tap-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.edge-tap-head b { margin-right: auto; }
/* kt-info-btn / kt-legend: same look as KtTrackingPanel's and PricingSourcePanel's (i) legend (scoped copy). */
.kt-info-btn { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; padding: 0;
  border-radius: 50%; background: transparent; color: #888; border: 1px solid #555;
  font-family: 'Times New Roman', Times, serif; font-style: italic; cursor: pointer; }
.kt-info-btn:hover, .kt-info-btn[aria-expanded="true"] { color: #e6edf3; border-color: #999; background: #ffffff10; }
.kt-info-btn:focus-visible { outline: 2px solid #58a6ff; outline-offset: 2px; }
.kt-legend { background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 0.75rem 0.9rem;
  font-size: 0.8rem; color: #ccc; line-height: 1.5; margin-top: 0.5rem; }
.kt-legend-lead { margin: 0 0 0.65rem 0; color: #b3b3b3; }
.edge-svg { width: 100%; height: auto; display: block; background: #0f0f0f; border: 1px solid #222; border-radius: 4px; }
.edge-tick { fill: #888; font-size: 10px; }
.edge-axis { fill: #ccc; font-size: 11px; }
.edge-strong { fill: #4caf50; font-size: 9px; opacity: 0.7; }
.edge-quadrant { fill: #777; font-size: 10px; font-weight: 600; }
.edge-quadrant-weight { fill: #888; font-size: 10px; }
.edge-label { font-size: 10px; font-weight: 600; pointer-events: none; }
.edge-bubble { cursor: pointer; }
.edge-note { color: #888; font-size: 12px; }
.edge-key { display: flex; flex-wrap: wrap; gap: 4px 10px; align-items: center; margin: 0 0 6px; font-size: 12px; color: #888; }
.edge-key-item { display: inline-flex; align-items: center; gap: 4px; color: #ccc; }
.edge-key-item i { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.edge-error { color: #f44336; font-size: 12px; }
.edge-tip { position: absolute; z-index: 5; pointer-events: none; background: #1c1c1c; border: 1px solid #333; border-radius: 4px;
  padding: 6px 8px; font-size: 11px; color: #ddd; max-width: 320px; }
.edge-tip-sub { display: block; color: #888; margin-bottom: 2px; }
.edge-tip-k { color: #888; }
@media (max-width: 480px) {
  /* the 760-unit viewBox renders at ~0.45 scale on a phone: enlarge text in viewBox units to stay legible */
  .edge-tick, .edge-quadrant, .edge-quadrant-weight { font-size: 18px; }
  .edge-label { font-size: 20px; }
  .edge-axis { font-size: 18px; }
  .edge-strong { font-size: 16px; }
  .edge-quadrant-long { display: none; }
}
@media (max-width: 380px) {
  .edge-quadrant-long { display: none; }
  .edge-gap { margin-left: 4px; }
  .edge-tip { position: static; max-width: none; margin-top: 6px; }
}
</style>
